/**
 * Single source for premium purchase-funnel analytics.
 *
 * Everything is pushed to the GTM dataLayer for GA4 ecommerce. Purchase also
 * calls Meta/Facebook Pixel directly because ad optimization needs the standard
 * `Purchase` event as soon as payment succeeds.
 *
 * Success-side events (`purchase`, `order_acknowledged`) fire at most once per
 * order via sessionStorage, so page reloads, the redirect timer, and the
 * "return home" button can no longer double-count conversions (#566).
 *
 * `fb_*` fields are emitted transitionally so existing Meta tags keep working.
 * Once GTM maps Meta off the GA4 `ecommerce` object, delete `metaFields()` and
 * its spreads — that is the final step of #556 (see the GTM checklist).
 */

type DataLayerEvent = Record<string, unknown>;

function push(event: DataLayerEvent) {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
}

/**
 * GA4 merges a new `ecommerce` object into whatever the previous push left
 * behind, so items leak across events — a session goes initiate_checkout ->
 * purchase and the second inherits the first's items. Clearing first is
 * Google's documented requirement, not a precaution.
 */
function pushEcommerce(event: DataLayerEvent) {
    push({ ecommerce: null });
    push(event);
}

/**
 * Run `fn` at most once per `key` for this browser session. A falsy `key` means
 * "no unique order id available" — fire WITHOUT deduping, because suppressing a
 * real conversion is worse than a rare double-count. (The normal success flow
 * always has a real `oid`, so this fallback only hits edge paths.)
 */
function once(key: string | null | undefined, fn: () => void) {
    if (typeof window === "undefined") return;
    if (!key) {
        fn();
        return;
    }
    try {
        const k = `sc_evt:${key}`;
        if (window.sessionStorage.getItem(k)) return;
        window.sessionStorage.setItem(k, "1");
    } catch {
        // sessionStorage blocked (private mode / quota): fall through and fire
        // anyway rather than drop a conversion. Worst case is the old behaviour.
    }
    fn();
}

export interface OrderItem {
    /** ProductId — identifies the plan, NOT unique per purchase. */
    id: number | string;
    title: string;
    /** unit/plan price; used for begin_checkout value */
    price: number;
    /** charged total for the purchase event; defaults to `price` */
    total?: number;
    /** real unique purchase id (from the server) — the dedupe + transaction key */
    orderId?: number | string;
    /** a new web sale Meta should count; it shares `orderId` as the event id with the server event */
    metaPurchase?: boolean;
}

// ponytail: transitional Meta payload — delete once GTM reads `ecommerce`. (#556)
function metaFields(fbEvent: string, value: number, item: OrderItem) {
    return {
        fb_event: fbEvent,
        fb_value: value,
        fb_currency: "TRY",
        fb_content_ids: [item.id.toString()],
        fb_content_type: "product",
        fb_content_name: item.title,
    };
}

// eventID matches the Conversions API event (meta-capi.ts), so Meta keeps one.
function trackMetaPurchase(value: number, item: OrderItem) {
    if (typeof window === "undefined") return;
    if (!item.metaPurchase || !item.orderId) return;
    window.fbq?.(
        "track",
        "Purchase",
        {
            value,
            currency: "TRY",
            content_ids: [item.id.toString()],
            content_name: item.title,
            content_type: "product",
        },
        { eventID: item.orderId.toString() },
    );
}

function ecommerceItem(item: OrderItem, price: number) {
    return {
        item_id: item.id.toString(),
        item_name: item.title,
        item_category: "premium",
        price,
        quantity: 1,
    };
}

/** Plan selected → checkout begins. Fires every time (explicit user action). */
export function trackBeginCheckout(item: OrderItem) {
    const value = Number(item.price) || 0;
    push({ event: "order_initiated", value: item.id });
    pushEcommerce({
        event: "initiate_checkout",
        ecommerce: {
            currency: "TRY",
            value,
            items: [ecommerceItem(item, value)],
        },
        ...metaFields("InitiateCheckout", value, item),
    });
}

/**
 * The iyzico-hosted form finished mounting, i.e. initialize() returned real
 * form content and the buyer can now enter a card.
 *
 * Without this there is nothing at all between `initiate_checkout` and
 * `purchase`, so every failure inside checkout is indistinguishable from
 * abandonment. On 2026-08-05 iyzico's subscription endpoint hung and eight
 * attempts died on a 20s timeout; in GA4 those buyers looked like people who
 * simply changed their mind.
 *
 * No monetary `value` here on purpose — see #941.
 */
export function trackCheckoutFormLoaded(item: OrderItem) {
    push({
        event: "checkout_form_loaded",
        plan_id: item.id.toString(),
        plan_name: item.title,
    });
}

/**
 * Checkout could not be started. `reason` is the message the buyer was shown,
 * so a funnel drop can be attributed to a cause (upstream timeout, rejected
 * coupon, missing buyer details) instead of guessed at.
 */
export function trackCheckoutFailed(item: OrderItem, reason: string) {
    push({
        event: "checkout_failed",
        plan_id: item.id.toString(),
        plan_name: item.title,
        failure_reason: reason.slice(0, 120),
    });
}

/** Payment succeeded — GA4 `purchase`, once per order. Skipped for free (0) orders. */
export function trackPurchase(item: OrderItem) {
    const value = Number(item.total ?? item.price) || 0;
    if (value <= 0) return;
    // transaction_id and dedupe key use the real order id; ProductId would
    // collide across two purchases of the same plan (and GA4 dedupes on it).
    const txId = item.orderId ? item.orderId.toString() : item.id.toString();
    once(item.orderId ? `purchase:${item.orderId}` : null, () => {
        pushEcommerce({
            event: "purchase",
            ecommerce: {
                transaction_id: txId,
                currency: "TRY",
                value,
                items: [ecommerceItem(item, value)],
            },
            ...metaFields("Purchase", value, item),
        });
        trackMetaPurchase(value, item);
    });
}

/** Reached the confirmation page — semantic event, once per order. */
export function trackOrderAcknowledged(
    orderId: number | string | null | undefined,
) {
    once(orderId ? `ack:${orderId}` : null, () =>
        push({ event: "order_acknowledged", value: orderId ?? null }),
    );
}

/**
 * `reason` is the stable machine code the server put on the redirect URL (see
 * settle() in checkout-cf.ts) — `declined`, `fraud`, `signature`, and so on —
 * never iyzico's own message, which is localized and would fragment the report
 * exactly the way the signup events avoid.
 */
export function trackOrderCancelled(
    orderId: number | string,
    reason?: string | null,
) {
    push({
        event: "order_cancelled",
        value: orderId,
        failure_reason: reason?.slice(0, 120) ?? "unknown",
    });
}

export function trackOrderFailed(
    orderId: number | string,
    reason?: string | null,
) {
    push({
        event: "order_failed",
        value: orderId,
        failure_reason: reason?.slice(0, 120) ?? "unknown",
    });
}

/**
 * Signup funnel (#945). Every event carries the UI locale so AZ vs TR
 * conversion is directly segmentable — the reason these exist before the AZ
 * launch. No monetary `value` on any of them (see #941).
 *
 * `reason` is a stable machine code (better-auth error code, or one of our
 * own like "client_validation:username"), NOT the localized message — the
 * message varies by locale and would fragment the report.
 */
export function trackSignUpStarted(locale: string) {
    push({ event: "sign_up_started", locale });
}

export function trackSignUpCompleted(locale: string) {
    push({ event: "sign_up_completed", locale });
}

export function trackSignUpFailed(locale: string, reason: string) {
    push({
        event: "sign_up_failed",
        locale,
        failure_reason: reason.slice(0, 120),
    });
}
